import Honeybadger from "@honeybadger-io/js"
import { removeRetryBanner, showRetryBanner } from "../lib/retry_banner"

const RETRY_DELAYS = [5_000, 25_000, 120_000, 300_000] // 5s, 25s, 2min, 5min
const SIGN_IN_PATH = "/sign_in"
const originalFrameSrcs = new Map()
const retryStates = new Map()
let visitingSignIn = false

// Capture original frame source URLs before Turbo fetches them.
// By the time turbo:frame-missing fires, the frame's src has already
// been updated to the redirect target (e.g., /sign_in), so we need
// to store the original URL here.
document.addEventListener("turbo:before-fetch-request", event => {
  if (event.target.tagName !== "TURBO-FRAME") return

  const frameId = event.target.id
  if (frameId && !originalFrameSrcs.has(frameId)) {
    originalFrameSrcs.set(frameId, event.detail.url.href)
  }
})

// Clean up retry state when a frame loads successfully
document.addEventListener("turbo:frame-load", event => {
  if (retryStates.delete(event.target.id) && retryStates.size === 0) {
    removeRetryBanner()
  }
})

// Clean up all state on page navigation
document.addEventListener("turbo:visit", () => {
  retryStates.forEach(state => clearTimeout(state.timer))
  retryStates.clear()
  originalFrameSrcs.clear()
  removeRetryBanner()
})

// Reset the sign-in dedupe flag once a page has finished rendering, so a
// later session expiry (Turbo keeps this module alive across visits) can
// trigger a fresh sign-in visit.
document.addEventListener("turbo:load", () => {
  visitingSignIn = false
})

// Retry frame loads that fail transiently: a redirect (e.g. session expiry)
// or a 5xx (app restart/deploy — Render answers 502 while the boot happens).
document.addEventListener("turbo:frame-missing", event => {
  const { response } = event.detail
  if (!response.redirected && response.status < 500) return

  event.preventDefault()

  // A redirect to sign-in means the session expired — no number of retries
  // can fix that. Re-visit the current page so the server redirects to
  // sign-in with a return_to for the page the user was actually on. Not an
  // app error, so no Honeybadger report.
  if (isSignInRedirect(event.detail.response)) {
    if (!visitingSignIn) {
      visitingSignIn = true
      window.Turbo.visit(window.location.href, { action: "replace" })
    }
    return
  }

  const frame = event.target
  const originalSrc = originalFrameSrcs.get(frame.id)
  if (!originalSrc) return

  let state = retryStates.get(frame.id)
  if (!state) {
    state = { attempt: 0, originalSrc, timer: null }
    retryStates.set(frame.id, state)
  }

  if (state.attempt >= RETRY_DELAYS.length) {
    updateRetryBanner(true)
    notifyHoneybadger(frame, event.detail.response)
    return
  }

  const delay = RETRY_DELAYS[state.attempt]
  state.attempt++
  updateRetryBanner(false)

  state.timer = setTimeout(() => {
    frame.removeAttribute("complete")
    frame.src = state.originalSrc
  }, delay)
})

function isSignInRedirect(response) {
  return new URL(response.url, window.location.origin).pathname === SIGN_IN_PATH
}

function updateRetryBanner(final) {
  showRetryBanner(
    final
      ? "We're having trouble loading some content. Please try refreshing the page or contact support if this continues."
      : "We're having trouble loading some content. Retrying shortly&hellip;",
    {final}
  )
}

function notifyHoneybadger(frame, response) {
  Honeybadger.notify({
    message: "Turbo frame missing after retries exhausted",
    name: "turbo:frame-missing",
    context: {
      targetId: frame.id,
      targetSource: frame.src,
      responseUrl: response.url,
      responseRedirected: response.redirected,
      responseStatus: response.status,
    }
  })
}
